import React, { useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Row,
  Card,
  Col,
  Typography,
  Divider,
  Table,
  Modal,
  Button,
} from "antd";
import {
  PaperClipOutlined,
  BankOutlined,
  BookOutlined,
} from "@ant-design/icons";
import Navbar from "../../components/Navbar/Navbar";
import tempImg from "../../assets/images/user-profile.jpg";
import { UseGetRequest, UsePostRequest } from "../../utils/api/ApiRequests"; // used to GET data with that uses 'post'
import moment from "moment";
import { Document, Page, pdfjs } from "react-pdf";
import { saveAs } from "file-saver";
import {
  capitalizeEachWord,
  formatAmount,
  formatDate,
  getEmployeeId,
} from "../../utils/Common";

const { Title } = Typography;
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.js",
  import.meta.url
).toString();

const defaultMedicalRecord = [
  { type: "CBC", key: "cbc", date: null },
  { type: "Fasting Blood Sugar", key: "fasting_blood_sugar", date: null },
  { type: "Drug Test", key: "drug_test", date: null },
  { type: "Chext Xray", key: "chest_xray", date: null },
  { type: "ECG", key: "ecg", date: null },
  { type: "Spirometry", key: "spirometry", date: null },
  { type: "Audiometry", key: "audiometry", date: null },
  { type: "Medical Certificate", key: "medical_certificate", date: null },
  { type: "Physical Exam", key: "physical_exam", date: null },
];

export default function EmployeeProfile() {
  const passedParams = useParams();
  const employeeId = getEmployeeId();
  const printRef = useRef();
  const navigate = useNavigate();
  const passedId = passedParams?.id ? passedParams.id : employeeId;
  const [employeeDetails, setEmployeeDetails] = useState({});
  const [employmentHistory, setEmploymentHistory] = useState([]);
  const [medicalRecord, setMedicalRecord] = useState(defaultMedicalRecord);
  const [requirements, setRequirements] = useState([]);
  const [exitDocuments, setExitDocuments] = useState([]);
  const [commLetters, setCommLetters] = useState([]);
  const [fileUrl, setFileUrl] = useState(null);
  const [previewFile, setPreviewFile] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [selectedFileType, setSelectedFileType] = useState(null);

  const employmentHistoryTable = [
    {
      title: "Period",
      dataIndex: "period",
      key: "period",
      render: (text, record) =>
        text ? moment(text).format("MMMM D, yyyy") : "",
    },
    {
      title: "Position",
      dataIndex: "position",
      key: "position",
    },
    {
      title: "Salary",
      dataIndex: "salary",
      key: "salary",
      render: (text) => (text ? formatAmount(text) : ""),
    },
  ];

  const requirementsTable = [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Attachments",
      dataIndex: "files",
      key: "files",
      render: (data, record) => (
        <div>
          {data ? (
            data.map((file) => (
              <Row className="">
                <PaperClipOutlined />
                <div
                  className="pointer-cursor ms-2 smaller-text"
                  onClick={() => handleFilePreview(file)}
                >
                  {file.file_name}
                </div>
              </Row>
            ))
          ) : (
            <Row className="smaller-text">No Attachments</Row>
          )}
        </div>
      ),
    },
  ];

  const medicalTable = [
    {
      title: "Type",
      key: "type",
      dataIndex: "type",
    },
    {
      title: "Date",
      key: "date",
      dataIndex: "date",
      render: (data) => <div>{data ? moment(data).format("MMMM DD") : ""}</div>,
    },
  ];

  const commTable = [
    {
      title: "Type",
      dataIndex: "letter_type_name",
      key: "letter_type_name",
    },
    {
      title: "Date Posted",
      dataIndex: "letter_date",
      key: "letter_date",
      render: (letter_date, record) => (
        <div>{moment(letter_date).format("MMM DD, YYYY")}</div>
      ),
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
    },
    {
      title: "Prepared By",
      dataIndex: "added_by_name",
      key: "added_by_name",
      render: (data, record) => (
        <div>
          <Row>{data}</Row>
          <Row>{moment(record.added_on).format("MMM DD, YYYY hh:mm A")}</Row>
        </div>
      ),
    },
  ];

  function handleFilePreview(file) {
    setPreviewFile(file);
    const fileType = file.file_name.split(".");
    setSelectedFileType(fileType[1]);
    setShowPreview(true);
  }

  function handleDownload() {
    saveAs(previewFile.file_url, previewFile.file_name);
  }

  const PDFViewer = ({ fileUrl }) => {
    const [numPages, setNumPages] = useState(null);

    const onDocumentLoadSuccess = ({ numPages }) => {
      setNumPages(numPages);
    };

    const renderPages = () => {
      const pages = [];
      for (let pageNumber = 1; pageNumber <= numPages; pageNumber += 2) {
        pages.push(
          <Row key={`row_${pageNumber}`} gutter={[16, 16]}>
            <Col span={12}>
              <Page key={`page_${pageNumber}`} pageNumber={pageNumber}>
                <div className="pdf-page-number">
                  Page {pageNumber} of {numPages}
                </div>
              </Page>
            </Col>
            {pageNumber + 1 <= numPages && (
              <Col span={12}>
                <Page
                  key={`page_${pageNumber + 1}`}
                  pageNumber={pageNumber + 1}
                >
                  <div className="pdf-page-number">
                    Page {pageNumber} of {numPages}
                  </div>
                </Page>
              </Col>
            )}
          </Row>
        );
      }
      return pages;
    };

    return (
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Document
            file={fileUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            className="w-100"
          >
            {numPages && renderPages()}
          </Document>
        </Col>
      </Row>
    );
  };

  async function fetchEmployeeData() {
    const response = await UsePostRequest("employees/get", {
      employee_id: passedId,
    });
    if (response.data) {
      const res = response.data[0];
      const tempName = `${res.first_name} ${res.middle_name} ${res.last_name}`;
      res.fullname = capitalizeEachWord(tempName);
      setEmployeeDetails(res);
    }
  }

  async function fetchEmploymentHistory() {
    const response = await UsePostRequest(
      "employees/employment_history/" + passedId
    );
    setEmploymentHistory([]);
    if (response.data) {
      const res = response.data;
      const result = res.map((data, index) => {
        let info = [];
        info.period = data.doc_date;
        info.salary = data.value_to;
        info.position = data.position_to;
        return info;
      });
      setEmploymentHistory(result);
    }
  }

  async function fetchAttachments() {
    setRequirements([]);
    const response = await UsePostRequest(
      "employees/get_employee_requirements",
      { employee_id: passedId }
    );
    if (response.data) {
      const res = response.data[0];
      const tempAttachments = [
        {
          type: "Valid ID",
          files: res.valid_id,
        },
        {
          type: "NBI Clearance",
          files: res.nbi_clearance,
        },
        {
          type: "Police Clearance",
          files: res.police_clearance,
        },
        {
          type: "Work Certification",
          files: res.work_certification,
        },
        {
          type: "Training Certification",
          files: res.training_certification,
        },
        {
          type: "Bonds",
          files: res.bonds,
        },
        {
          type: "Contract",
          files: res.contract,
        },
      ];
      setRequirements(tempAttachments);
    }
  }

  async function fetchExitDocuments() {
    setExitDocuments([]);
    const response = await UseGetRequest(
      "employees/get_employee_exit_docs",
      { employee_id: passedId }
    );
    if (response.data) {
      const res = response.data.employees[0];
      const tempAttachments = [
        {
          type: "Release and Quit Claim",
          files: res.release,
        },
        {
          type: "Clearance",
          files: res.clearance,
        },
        {
          type: "COE",
          files: res.coe,
        },
      ];
      setExitDocuments(tempAttachments);
    }
  }

  async function fetchProfilePicture() {
    const response = await UseGetRequest("employees/get_employee_picture", {
      employee_id: passedId,
    });
    if (response.data) {
      setFileUrl(response.data.file_url);
    } else {
      setFileUrl(null);
    }
  }

  async function fetchMedicalRecords() {
    setMedicalRecord([]);
    const response = await UseGetRequest("employees/get_medical_record", {
      employee_id: passedId,
    });
    if (response.data) {
      const medRecord = response.data.medical_record[0];
      const tempArray = [
        { type: "CBC", key: "cbc", date: medRecord.cbc },
        {
          type: "Fasting Blood Sugar",
          key: "fasting_blood_sugar",
          date: medRecord.fasting_blood_sugar,
        },
        {
          type: "Drug Test",
          key: "drug_test",
          date: medRecord.drug_test,
        },
        {
          type: "Chext Xray",
          key: "chest_xray",
          date: medRecord.chest_xray,
        },
        { type: "ECG", key: "ecg", date: medRecord.ecg },
        {
          type: "Spirometry",
          key: "spirometry",
          date: medRecord.spirometry,
        },
        {
          type: "Audiometry",
          key: "audiometry",
          date: medRecord.audiometry,
        },
        {
          type: "Medical Certificate",
          key: "medical_certificate",
          date: medRecord.medical_certificate,
        },
        {
          type: "Physical Exam",
          key: "physical_exam",
          date: medRecord.physical_exam,
        },
      ];
      setMedicalRecord(tempArray);
    }
  }

  async function fetchCommLetters() {
    setCommLetters([]);
      const response = await UseGetRequest("letters/get", {employee_id: passedId, status: 'approved'});
      if (response.data) {
        const res = response.data.data?.letters?.map((data) => {
          const info = data;
          info.recipients = data.details.map((rec) => {
            let recInfo = {};
            recInfo.type = rec.type;
            recInfo.letter_to = rec.letter_to_id;
            return recInfo;
          });
          return info;
        });
        setCommLetters(res);
      }
  }

  React.useEffect(() => {
    fetchEmployeeData();
    fetchEmploymentHistory();
    fetchAttachments();
    fetchProfilePicture();
    fetchMedicalRecords();
    fetchExitDocuments();
    fetchCommLetters();
  }, []);

  return (
    <Navbar
    activeNav="leave-credits"
      formTitle="Employee Profile"
      isForm
      withPrintBtn
      printContent={printRef}
      printText="Print 201"
    >
      <Row className="view-page" ref={printRef}>
        <Col lg={4} sm={4} xs={24}>
          <Card className="profile-pic-container">
            <img
              src={fileUrl ? fileUrl : tempImg}
              alt=""
              className="profile-picture"
              width={350}
              height={350}
            />
          </Card>
        </Col>
        <Col lg={20} sm={24} xs={24}>
          <Card className="mx-1">
            <Row>
              <Col lg={12} sm={12} xs={24}>
                <Row>
                  <Col lg={5} className="bold">
                    Name
                  </Col>
                  <Col lg={1}>:</Col>
                  <Col lg={17}>{employeeDetails.fullname}</Col>
                </Row>
                <Row>
                  <Col lg={5} className="bold">
                    Religion
                  </Col>
                  <Col lg={1}>:</Col>
                  <Col lg={17}>
                    {capitalizeEachWord(employeeDetails.religion)}
                  </Col>
                </Row>
                <Row>
                  <Col lg={5} className="bold">
                    Civil Status
                  </Col>
                  <Col lg={1}>:</Col>
                  <Col lg={17}>
                    {capitalizeEachWord(employeeDetails.civil_status)}
                  </Col>
                </Row>
              </Col>
              <Col lg={12} sm={12} xs={24}>
                <Row>
                  <Col lg={5} className="bold">
                    Gender
                  </Col>
                  <Col lg={1}>:</Col>
                  <Col lg={17}>
                    {capitalizeEachWord(employeeDetails.gender)}
                  </Col>
                </Row>
                <Row>
                  <Col lg={5} className="bold">
                    Birthdate
                  </Col>
                  <Col lg={1}>:</Col>
                  <Col lg={17}>
                    {moment(employeeDetails.birthdate).format("MMMM DD, YYYY")}
                  </Col>
                </Row>
                <Row>
                  <Col lg={5} className="bold">
                    Nationality
                  </Col>
                  <Col lg={1}>:</Col>
                  <Col lg={17}>
                    {capitalizeEachWord(employeeDetails.nationality)}
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card>
          <Row>
            <Col lg={12} sm={12} xs={24}>
              <Card className="mt-2 mx-1 smaller-font-card">
                <Title level={5}>Contact Details</Title>
                <Divider />
                <Row>
                  <Col lg={7} className="bold">
                    Contact No.
                  </Col>
                  <Col lg={1}>:</Col>
                  <Col lg={15}>{employeeDetails.contact_no}</Col>
                </Row>
                <Row>
                  <Col lg={7} className="bold">
                    Email Address
                  </Col>
                  <Col lg={1}>:</Col>
                  <Col lg={15}>{employeeDetails.email}</Col>
                </Row>
                <Row>
                  <Col lg={7} className="bold">
                    Address
                  </Col>
                  <Col lg={1}>:</Col>
                  <Col lg={15}>{employeeDetails.address}</Col>
                </Row>
              </Card>
            </Col>
            <Col lg={12} sm={12} xs={24}>
              <Card className="mt-2 mx-1 smaller-font-card">
                <Title level={5}>Emergency Contact</Title>
                <Divider />
                <Row>
                  <Col lg={7} className="bold">
                    Contact Person
                  </Col>
                  <Col lg={1}>:</Col>
                  <Col lg={15}>{employeeDetails.contact_person}</Col>
                </Row>
                <Row>
                  <Col lg={7} className="bold">
                    Relationship
                  </Col>
                  <Col lg={1}>:</Col>
                  <Col lg={15}>
                    {capitalizeEachWord(
                      employeeDetails.contact_person_relationship
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col lg={7} className="bold">
                    Contact No.
                  </Col>
                  <Col lg={1}>:</Col>
                  <Col lg={15}>{employeeDetails.contact_person_no}</Col>
                </Row>
                <Row>
                  <Col lg={7} className="bold">
                    Address
                  </Col>
                  <Col lg={1}>:</Col>
                  <Col lg={15}>
                    {capitalizeEachWord(employeeDetails.contact_person_address)}
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
          <Card className="mx-1 mt-2 smaller-font-card">
            <Title level={5}>Family Relationship Details</Title>
            <Divider />
            <Row className="d-flex justify-content-between">
              <Col xs={24} sm={12} lg={12}>
                <span className="bold me-1">Mother's Name :</span>{capitalizeEachWord(employeeDetails.mothers_name)}
              </Col>
              <Col xs={6} sm={3} lg={6}>
                <span className="bold me-1">Age :</span>{employeeDetails.mothers_age}
              </Col>
              <Col xs={18} sm={9} lg={6}>
                <span className="bold me-1">Occupation :</span>{capitalizeEachWord(employeeDetails.mothers_occupation)}
              </Col>
            </Row>
            <Row className="d-flex justify-content-between">
              <Col xs={24} sm={12} lg={12}>
                <span className="bold me-1">Father's Name :</span>{capitalizeEachWord(employeeDetails.fathers_name)}
              </Col>
              <Col xs={6} sm={3} lg={6}>
                <span className="bold me-1">Age :</span>{employeeDetails.fathers_age}
              </Col>
              <Col xs={18} sm={9} lg={6}>
                <span className="bold me-1">Occupation :</span>{capitalizeEachWord(employeeDetails.fathers_occupation)}
              </Col>
            </Row>
            <Row className="d-flex justify-content-between">
              <Col xs={24} sm={12} lg={12}>
                <span className="bold me-1">Spouse's Name :</span>{capitalizeEachWord(employeeDetails.spouse_name)}
              </Col>
              <Col xs={6} sm={3} lg={6}>
                <span className="bold me-1">Age :</span>{employeeDetails.spouse_age}
              </Col>
              <Col xs={18} sm={9} lg={6}>
                <span className="bold me-1">Occupation :</span>{capitalizeEachWord(employeeDetails.spouse_occupation)}
              </Col>
            </Row>
            {/* <Row>
              <Col lg={3} sm={3} md={6} xs={11} className="bold">
                Spouse's Name
              </Col>
              <Col lg={1} sm={1}>
                :
              </Col>
              <Col lg={6} sm={6} md={17} xs={12}>
                {capitalizeEachWord(employeeDetails.spouse_name)}
              </Col>
              <Col lg={1} sm={1} md={3} xs={3} className="bold">
                Age
              </Col>
              <Col lg={1} sm={1}>
                :
              </Col>
              <Col lg={2} sm={2} xs={3}>
                {employeeDetails.spouse_age}
              </Col>
              <Col lg={2} sm={2} className="bold">
                Occupation
              </Col>
              <Col lg={1} sm={1}>
                :
              </Col>
              <Col lg={6} sm={6}>
                {capitalizeEachWord(employeeDetails.spouse_occupation)}
              </Col>
            </Row> */}
            {employeeDetails.children &&
              employeeDetails.children.map((row) => (
                <Row className="d-flex justify-content-between">
                  <Col xs={24} sm={12} lg={12}>
                    <span className="bold me-1">Child's Name :</span>{capitalizeEachWord(row.name)}
                  </Col>
                  <Col xs={6} sm={3} lg={6}>
                    <span className="bold me-1">Age :</span>{row.age}
                  </Col>
                  <Col xs={18} sm={9} lg={6}>
                    <span className="bold me-1">Occupation :</span>{capitalizeEachWord(row.occupation)}
                  </Col>
                </Row>
              ))}
          </Card>
          <Row>
            <Col lg={12} sm={12} xs={24}>
              <Card className="mt-2 mx-1 smaller-font-card">
                <Title level={5}>Educational Background</Title>
                <Divider />
                {employeeDetails.employee_educ_background ? (
                  employeeDetails.employee_educ_background.map((row) => (
                    <Row>
                      <BookOutlined className="me-2" />
                      {row.description}
                    </Row>
                  ))
                ) : (
                  <Row className="center no-data">No data</Row>
                )}
              </Card>
            </Col>
            <Col lg={12} sm={12} xs={24}>
              <Card className="mt-2 mx-1 smaller-font-card">
                <Title level={5}>Previous Works</Title>
                <Divider />
                {employeeDetails.employee_work_background ? (
                  employeeDetails.employee_work_background.map((row) => (
                    <Row>
                      <BankOutlined className="me-2" /> {row.description}
                    </Row>
                  ))
                ) : (
                  <Row className="center no-data">No data</Row>
                )}
              </Card>
            </Col>
          </Row>
          <Row>
            <Col lg={14} md={24} sm={14} xs={24}>
              <Card className="mt-2 mx-1 smaller-font-card">
                <Title level={5}>
                  Employee Benefit Contribution Information
                </Title>
                <Divider />
                <Row>
                  <Col xs={12}>
                    <Row>
                      <Col className="bold">
                        SSS No.
                      </Col>
                      <Col lg={1}>:</Col>
                      <Col className="ms-2">
                        {employeeDetails.sss}
                      </Col>
                    </Row>
                    <Row>
                      <Col className="bold">
                        HDMF No.
                      </Col>
                      <Col lg={1}>:</Col>
                      <Col className="ms-2">
                        {employeeDetails.hdmf}
                      </Col>
                    </Row>
                    <Row>
                      <Col className="bold">
                        PHIC No.
                      </Col>
                      <Col lg={1}>:</Col>
                      <Col className="ms-2">
                        {employeeDetails.philhealth}
                      </Col>
                    </Row>
                    <Row>
                      <Col className="bold">
                        SSS EE Amount
                      </Col>
                      <Col lg={1}>:</Col>
                      <Col className="ms-2">
                        {employeeDetails.sss_ee_amount}
                      </Col>
                    </Row>
                    <Row>
                      <Col className="bold">
                        SSS ER Amount
                      </Col>
                      <Col lg={1}>:</Col>
                      <Col className="ms-2">
                        {employeeDetails.sss_er_amount}
                      </Col>
                    </Row>
                    <Row>
                      <Col className="bold">
                        HDMF EE Amount
                      </Col>
                      <Col lg={1}>:</Col>
                      <Col className="ms-2">
                        {employeeDetails.hdmf_ee_amount}
                      </Col>
                    </Row>
                  </Col>
                  <Col xs={12}>
                    <Row>
                      <Col className="bold">
                        HDMF ER Amount
                      </Col>
                      <Col lg={1}>:</Col>
                      <Col className="ms-2">
                        {employeeDetails.hdmf_er_amount}
                      </Col>
                    </Row>
                    <Row>
                      <Col className="bold">
                        PHIC EE Amount
                      </Col>
                      <Col lg={1}>:</Col>
                      <Col className="ms-2">
                        {employeeDetails.philhealth_ee_amount}
                      </Col>
                    </Row>
                    <Row>
                      <Col className="bold">
                        PHIC ER Amount
                      </Col>
                      <Col lg={1}>:</Col>
                      <Col className="ms-2">
                        {employeeDetails.philhealth_er_amount}
                      </Col>
                    </Row>
                    <Row>
                      <Col className="bold">
                        EC Amount
                      </Col>
                      <Col lg={1}>:</Col>
                      <Col className="ms-2">
                        {employeeDetails.ec_amount}
                      </Col>
                    </Row>
                    <Row>
                      <Col className="bold">
                        HMO
                      </Col>
                      <Col lg={1}>:</Col>
                      <Col className="ms-2">
                        {employeeDetails.is_generali === "0" ? "No" : "Yes"}
                      </Col>
                    </Row>
                    <Row>
                      <Col className="bold">
                        GPA
                      </Col>
                      <Col lg={1}>:</Col>
                      <Col className="ms-2">
                        {employeeDetails.is_gpa === "0" ? "No" : "Yes"}
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col lg={10} md={24} sm={10} xs={24}>
              <Card className="mt-2 mx-1 smaller-font-card">
                <Title level={5}>Withholding Tax Information</Title>
                <Divider />
                <Row>
                  <Col lg={6} className="bold">
                    TIN
                  </Col>
                  <Col lg={1}>:</Col>
                  <Col lg={15} className="ms-2">
                    {employeeDetails.tin}
                  </Col>
                </Row>
                <Row>
                  <Col lg={6} className="bold">
                    Status
                  </Col>
                  <Col lg={1}>:</Col>
                  <Col lg={15} className="ms-2">
                    {employeeDetails.wtaxstatus}
                  </Col>
                </Row>
                <Row>
                  <Col lg={6} className="bold">
                    Dependent
                  </Col>
                  <Col lg={1}>:</Col>
                  <Col lg={15} className="ms-2">
                    {employeeDetails.dependent}
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
          <Card className="mx-1 mt-2">
            <Title level={5}>Employment Information</Title>
            <Divider />
            <Row>
              <Col lg={12} sm={12} xs={24}>
                <Row>
                  <Col lg={8} xs={7} className="bold">
                    DTR ID{" "}
                  </Col>
                  <Col lg={1} xs={1}>
                    :
                  </Col>
                  <Col lg={14} xs={14} className="ms-2">
                    {employeeDetails.dtr_id}
                  </Col>
                </Row>
                <Row>
                  <Col lg={8} xs={7} className="bold">
                    Position
                  </Col>
                  <Col lg={1} xs={1}>
                    :
                  </Col>
                  <Col lg={14} xs={14} className="ms-2">
                    {employeeDetails.position === "CEO"
                      ? employeeDetails.position
                      : capitalizeEachWord(employeeDetails.position)}
                  </Col>
                </Row>
                <Row>
                  <Col lg={8} xs={7} className="bold">
                    Salary
                  </Col>
                  <Col lg={1} xs={1}>
                    :
                  </Col>
                  <Col lg={14} xs={14} className="ms-2">
                    PHP{" "}
                    {employeeDetails.basic_salary
                      ? formatAmount(employeeDetails.basic_salary)
                      : "0.00"}
                  </Col>
                </Row>
                <Row>
                  <Col lg={8} xs={7} className="bold">
                    Salary Type
                  </Col>
                  <Col lg={1} xs={1}>
                    :
                  </Col>
                  <Col lg={14} xs={14} className="ms-2">
                    {capitalizeEachWord(employeeDetails.salary_type)}
                  </Col>
                </Row>
                <Row>
                  <Col lg={8} xs={7} className="bold">
                    Bank
                  </Col>
                  <Col lg={1} xs={1}>
                    :
                  </Col>
                  <Col lg={14} xs={14} className="ms-2">
                    {employeeDetails.bank}
                  </Col>
                </Row>
                <Row>
                  <Col lg={8} xs={7} className="bold">
                    Bank Account Name
                  </Col>
                  <Col lg={1} xs={1}>
                    :
                  </Col>
                  <Col lg={14} xs={14} className="ms-2">
                    {capitalizeEachWord(employeeDetails.bank_account_name)}
                  </Col>
                </Row>
                <Row>
                  <Col lg={8} xs={7} className="bold">
                    Bank Account Number
                  </Col>
                  <Col lg={1} xs={1}>
                    :
                  </Col>
                  <Col lg={14} xs={14} className="ms-2">
                    {employeeDetails.bank_account_no}
                  </Col>
                </Row>
                <Row>
                  <Col lg={8} xs={7} className="bold">
                    Employment Status
                  </Col>
                  <Col lg={1} xs={1}>
                    :
                  </Col>
                  <Col lg={14} xs={14} className="ms-2">
                    {capitalizeEachWord(employeeDetails.employment_status)}
                  </Col>
                </Row>
              </Col>
              <Col lg={12} sm={12} xs={24}>
                <Row>
                  <Col lg={8} xs={7} className="bold">
                    Immediate Head
                  </Col>
                  <Col lg={1} xs={1}>
                    :
                  </Col>
                  <Col lg={14} xs={14} className="ms-2">
                    {capitalizeEachWord(employeeDetails.immediate_head)}
                  </Col>
                </Row>
                <Row>
                  <Col lg={8} xs={7} className="bold">
                    Date Hired
                  </Col>
                  <Col lg={1} xs={1}>
                    :
                  </Col>
                  <Col lg={14} xs={14} className="ms-2">
                    {formatDate(employeeDetails.date_hired)}
                  </Col>
                </Row>
                <Row>
                  <Col lg={8} xs={7} className="bold">
                    End of Contract
                  </Col>
                  <Col lg={1} xs={1}>
                    :
                  </Col>
                  <Col lg={14} xs={14} className="ms-2">
                    {employeeDetails.end_of_contract
                      ? formatDate(employeeDetails.end_of_contract)
                      : ""}
                  </Col>
                </Row>
                <Row>
                  <Col lg={8} xs={7} className="bold">
                    Date Regularized
                  </Col>
                  <Col lg={1} xs={1}>
                    :
                  </Col>
                  <Col lg={14} xs={14} className="ms-2">
                    {employeeDetails.date_regularized
                      ? formatDate(employeeDetails.date_regularized)
                      : ""}
                  </Col>
                </Row>
                <Row>
                  <Col lg={8} xs={7} className="bold">
                    Date Rehired
                  </Col>
                  <Col lg={1} xs={1}>
                    :
                  </Col>
                  <Col lg={14} xs={14} className="ms-2">
                    {employeeDetails.date_rehired
                      ? formatDate(employeeDetails.date_rehired)
                      : ""}
                  </Col>
                </Row>
                <Row>
                  <Col lg={8} xs={7} className="bold">
                    Date Terminated/Resigned
                  </Col>
                  <Col lg={1} xs={1}>
                    :
                  </Col>
                  <Col lg={14} xs={14} className="ms-2">
                    {employeeDetails.date_resigned_terminated
                      ? formatDate(employeeDetails.date_resigned_terminated)
                      : ""}
                  </Col>
                </Row>
                <Row>
                  <Col lg={8} xs={7} className="bold">
                    Vacation Leave Credits
                  </Col>
                  <Col lg={1} xs={1}>
                    :
                  </Col>
                  <Col lg={14} xs={14} className="ms-2">
                    {employeeDetails.vacation_leave_credits}
                  </Col>
                </Row>
                <Row>
                  <Col lg={8} xs={7} className="bold">
                    Sick Leave Credits
                  </Col>
                  <Col lg={1} xs={1}>
                    :
                  </Col>
                  <Col lg={14} xs={14} className="ms-2">
                    {employeeDetails.sick_leave_credits}
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card>

          <Card className="mx-1 mt-2 smaller-font-card">
            <Title level={5}>Projects and Allowances</Title>
            <Divider />
            <Row>
              <Col lg={6} sm={4} md={10} xs={14} className="bold">
                ECOLA
              </Col>
              <Col lg={1} xs={1}>
                :
              </Col>
              <Col xs={7} className="ms-2">
                PHP {formatAmount(employeeDetails.ecola_allowance ?? 0.0)}
              </Col>
            </Row>
            {employeeDetails.projects ? (
              employeeDetails.projects.map((row) => (
                <Row>
                  <Col lg={6} sm={4} md={10} xs={14} className="bold">
                    {row.project} Project
                  </Col>
                  <Col lg={1} xs={1}>
                    :
                  </Col>
                  <Col lg={12} xs={7} className="ms-2">
                    PHP {formatAmount(row.project_allowance ?? 0.0)}
                  </Col>
                </Row>
              ))
            ) : (
              <Row className="no-data">No data</Row>
            )}
          </Card>

          <Card className="mx-1 mt-2">
            <Title level={5}>Employment History</Title>
            <Divider />
            <Table
              dataSource={employmentHistory}
              columns={employmentHistoryTable}
            />
          </Card>
          <Card className="mx-1 mt-2">
            <Title level={5}>Communication Letters</Title>
            <Divider />
            <Table
              dataSource={commLetters}
              columns={commTable}
              onRow={(record) => ({
                onClick: () => {
                  window.open('/communication-letters/view/'+record.id, '_blank');
                },
                style: { cursor: 'pointer' },
              })} 
            />
          </Card>

          <Card className="mx-1 mt-2">
            <Title level={5}>Medical Record</Title>
            <Divider />
            <Table
              dataSource={medicalRecord}
              columns={medicalTable}
              pagination={false}
            />
          </Card>

          <div className="noPrint">
            <Card className="mx-1 mt-2">
              <Title level={5} className="">
                Uploaded Requirements
              </Title>
              <Divider />
              <Table
                dataSource={requirements}
                columns={requirementsTable}
                pagination={false}
              />
            </Card>
          </div>

          <div className="noPrint">
            <Card className="mx-1 mt-2">
              <Title level={5} className="">
                Exit Documents
              </Title>
              <Divider />
              <Table
                dataSource={exitDocuments}
                columns={requirementsTable}
                pagination={false}
              />
            </Card>
          </div>

          <div className="mx-1 mt-3 printOnly">
            <Row className="print-title">Uploaded Requirements</Row>
            <Divider />
            {requirements.map((row, rowIndex) => {
              return (
                <Row key={`row_${rowIndex}`} className="mt-1">
                  <Row className="bold print-text">{row.files && row.type}</Row>
                  <Row>
                    {row.files &&
                      row.files.map((file, fileIndex) => {
                        const fileType = file.file_name
                          .split(".")
                          .pop()
                          .toLowerCase();
                        if (fileType === "pdf") {
                          return (
                            <Col key={`pdf_${fileIndex}`} xs={24} className="">
                              <PDFViewer fileUrl={file.file_url}/>
                            </Col>
                          );
                        } else {
                          return (
                            <Col
                              key={`image_${fileIndex}`}
                              lg={4}
                              sm={6}
                              md={6}
                              xs={12}
                              className="pdf-image-container m-1"
                            >
                              <img
                                src={file.file_url}
                                alt={file.file_name}
                                className="w-100"
                              />
                            </Col>
                          );
                        }
                      })}
                  </Row>
                </Row>
              );
            })}
          </div>

          <div className="mx-1 mt-3 printOnly">
            <Row className="print-title">Exit Documents</Row>
            <Divider />
            {exitDocuments.map((row, rowIndex) => {
              return (
                <Row key={`row_${rowIndex}`} className="mt-1">
                  <Row className="bold print-text">{row.files && row.type}</Row>
                  <Row>
                    {row.files &&
                      row.files.map((file, fileIndex) => {
                        const fileType = file.file_name
                          .split(".")
                          .pop()
                          .toLowerCase();
                        if (fileType === "pdf") {
                          return (
                            <Col key={`pdf_${fileIndex}`} xs={24} className="">
                              <PDFViewer fileUrl={file.file_url} />
                            </Col>
                          );
                        } else {
                          return (
                            <Col
                              key={`image_${fileIndex}`}
                              lg={4}
                              sm={6}
                              md={6}
                              xs={12}
                              className="pdf-image-container m-1"
                            >
                              <img
                                src={file.file_url}
                                alt={file.file_name}
                                className="w-100"
                              />
                            </Col>
                          );
                        }
                      })}
                  </Row>
                </Row>
              );
            })}
          </div>
        </Col>
      </Row>

      <Modal
        open={showPreview}
        onCancel={() => setShowPreview(false)}
        footer={null}
      >
        <Row>
          <Col xs={24}>
            {selectedFileType === "pdf" ? (
              <iframe
                src={previewFile.file_url}
                className="pdf-preview"
                width="100%"
                height="600px"
                title={previewFile}
              />
            ) : (
              <>
                <img
                  src={previewFile.file_url}
                  alt="img-preview"
                  className="w-100"
                />
                <Row className="justify-content-end">
                  <Button type="primary" onClick={() => handleDownload()}>
                    Download
                  </Button>
                </Row>
              </>
            )}
          </Col>
        </Row>
      </Modal>
    </Navbar>
  );
}
